import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { ArrowDownAZ, FolderUp, FileUp, type LucideIcon } from 'lucide-react';
import type { SortOrder } from '@utils/localStorage/sort-order-storage.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';

interface SortOrderToggleProps {
  sortOrder: SortOrder;
  onSortOrderChange: (order: SortOrder) => void;
}

const sortOptions: {
  value: SortOrder;
  icon: LucideIcon;
  tooltipKey: string;
}[] = [
  { value: 'default', icon: ArrowDownAZ, tooltipKey: 'sort-by-date-tooltip' },
  {
    value: 'folders-first',
    icon: FolderUp,
    tooltipKey: 'folders-first-tooltip'
  },
  { value: 'files-first', icon: FileUp, tooltipKey: 'files-first-tooltip' }
];

export const SortOrderToggle = ({
  sortOrder,
  onSortOrderChange
}: SortOrderToggleProps) => {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();

  return (
    <div
      className="inline-flex items-center rounded-full border overflow-hidden"
      style={{
        borderColor: getColour('border'),
        backgroundColor:
          theme === 'dark' ? getColour('firstLayer') : getColour('secondLayer')
      }}
    >
      {sortOptions.map(({ value, icon: Icon, tooltipKey }) => {
        const isActive = sortOrder === value;
        return (
          <Tooltip key={value} label={t(tooltipKey)} placement="bottom">
            <button
              type="button"
              onClick={() => onSortOrderChange(value)}
              aria-label={t(tooltipKey)}
              aria-pressed={isActive}
              className="flex items-center justify-center w-8 h-[30px] cursor-pointer transition-all duration-150 hover:opacity-80 focus:outline-none focus:ring-0 focus-visible:outline-none"
              style={{
                backgroundColor: isActive
                  ? getColour('activeText')
                  : 'transparent',
                color: isActive
                  ? getColour('appBackground')
                  : getColour('inactiveText'),
                borderRight:
                  value !== 'files-first'
                    ? `1px solid ${getColour('border')}`
                    : 'none'
              }}
            >
              <Icon size={16} />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};
